// src/pages/About.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as THREE from "three";
import NET from "vanta/dist/vanta.net.min.js";

import me from "../assets/me2.jpg";
import originsImg from "../assets/Catedral_SLP.jpg";
import byuiImg from "../assets/BYUITaylor.jpeg";
import expSkillsImg from "../assets/skills.png";
import projectsImg from "../assets/comingsoon.png";
import consultancyImg from "../assets/comingsoon.png";
import teslaInternshipImg from "../assets/TeslaInternship.jpeg"; // optional: used inside Experience details
import PortfolioImg from "../assets/portfolio.jpg";

import "../styles/About.css";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function getCenter(el) {
  if (!el) return { x: 0, y: 0 };
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

export default function About() {
  const navigate = useNavigate();

  const bgRef = useRef(null);
  const vantaRef = useRef(null);

  const stageRef = useRef(null);
  const nodeRefs = useRef({});
  const [lines, setLines] = useState([]);

  // Active modal node id
  const [activeId, setActiveId] = useState(null);

  /**
   * Clockwise order:
   * Me -> Origins -> BYU-I -> Experience & Skills -> Projects -> Data Consultancy
   */
  const nodes = useMemo(
    () => [
      {
        id: "me",
        title: "Christian Lira Go.",
        subtitle: "Analytics Engineer",
        type: "profile",
        image: me,
        position: { x: 50, y: 16 },
        tag: "Me",
        details: (
          <>
            <p>
              I'm an Analytics Engineer who builds data platforms from pipeline to delivery.
      My work sits at the intersection of data engineering and business intelligence, 
      designing the infrastructure that makes analytics possible, not just the
      dashboards on top of it.
            </p>

            <ul>
              <li>ELT pipeline design · OLAP data modeling · API-driven data delivery</li>
              <li>Microsoft Fabric · Power BI semantic models · SQL Server · PostgreSQL</li>
              <li>Currently building Orpheus Analytics, an end-to-end economic intelligence platform</li>
              <li>Trilingual: English · Español (native) · 中文 (HSK4)</li>
            </ul>
          </>
        ),
      },

      {
        id: "origins",
        title: "Origins",
        subtitle: "San Luis Potosí, Mexico",
        image: originsImg,
        tag: "Story",
        position: { x: 22, y: 36 },
        details: (
  <>
    <p>
      I was born and raised in San Luis Potosí, an industrial and colonial city in
      north-central Mexico. Growing up there gave me early exposure to how
      infrastructure, labor, and institutions shape everyday outcomes, often
      imperfectly, but tangibly.
    </p>

    <p>
      That environment shaped how I think about data and systems today. I’m less
      interested in ideal models and more focused on how decisions actually get
      made inside real constraints: limited resources, legacy processes, and
      human behavior.
    </p>

    <p className="about-muted" style={{ marginTop: 10 }}>
      That perspective drives everything I build. Analytics tools that work inside
      real constraints, legacy systems, limited resources, and human behavior,
      not just clean demo environments. "The philosophers have hitherto only
      interpreted the world in various ways; the point, however, is to change it."
    </p>
  </>
),
      },

      {
        id: "byui",
        title: "BYU–Idaho",
        subtitle: "From the Abstract to the Tangible",
        image: byuiImg,
        tag: "Education",
        position: { x: 18, y: 62 },
        details: (
  <>
    <p>
      At BYU–Idaho I built an intentionally broad foundation: Business Analytics
      (BS) with minors in Data Science and Political Science, plus certificates
      in Statistics and Computer Programming. The interdisciplinary
      approach was deliberate, analytics problems rarely stay inside one discipline.
    </p>

    <p>
      Alongside coursework, I led the Analytics Engineering initiative for
      Facilities Management designing and building the centralized data
      architecture, ELT pipelines, and Power BI semantic models from scratch.
      That role is where the engineering identity solidified.
    </p>

    <p>
      That mindset continues to guide my education today. I approach learning as an
      ongoing process, constantly building depth while expanding across adjacent
      fields to stay adaptable as technology and analytics evolve.
    </p>

    <p className="about-muted" style={{ marginTop: 10 }}>
      Next: targeting OMSCS at Georgia Institute of Technology (MS Computer
      Science), deepening the systems and engineering foundations that
      complement applied analytics work.
    </p>
  </>
),
      },

      {
        id: "exp_skills",
        title: "Experience & Skills",
        subtitle: "Applied Data Systems & Delivery",
        image: expSkillsImg,
        tag: "Skills",
        position: { x: 50, y: 82 },
        details: (
  <>
    <p>
      My experience spans the full analytics engineering stack from pipeline
      architecture to semantic model delivery. I've worked on production systems
      at Tesla, led analytics infrastructure builds at BYU-Idaho, and am currently
      founding Orpheus Analytics as an end-to-end data platform.
    </p>

    <div style={{ marginTop: 12 }}>
      <div style={{ fontWeight: 800, marginBottom: 6 }}>Analytics Engineering</div>
      <p className="about-muted">
        Microsoft Fabric (Lakehouse, Pipelines, Data Warehouse) · Power BI (DAX,
        semantic models) · SSIS · Power Automate · OLAP/OLTP architecture ·
        ELT pipeline design · data validation frameworks
      </p>
    </div>

    <div style={{ marginTop: 12 }}>
      <div style={{ fontWeight: 800, marginBottom: 6 }}>Data Engineering</div>
      <p className="about-muted">
        Python (FastAPI, pandas, Docker) · SQL (T-SQL, PostgreSQL, MySQL) ·
        Apache Spark · Databricks · API integration · schema design ·
        medallion architecture
      </p>
    </div>

    <div style={{ marginTop: 12 }}>
      <div style={{ fontWeight: 800, marginBottom: 6 }}>Statistics & Analysis</div>
      <p className="about-muted">
        R (tidyverse, ggplot2, R Markdown) · statistical inference ·
        ARIMA / time series · econometrics · NLP (LDA topic modeling)
      </p>
    </div>

    <p className="about-muted" style={{ marginTop: 10 }}>
      I measure success by whether the system keeps working once it's deployed,
      not by how sophisticated the methodology looks on paper.
    </p>
  </>
),

      },

      {
        id: "projects",
        title: "My Projects",
        subtitle: "Professional Portfolio",
        image: PortfolioImg,
        tag: "Work",
        position: { x: 82, y: 62 },
        details: (
  <>
    <p>
      Projects ranging from production analytics platforms to econometric research,
      each one built end-to-end with real data, documented publicly on GitHub.
    </p>
    <ul>
      <li><strong>Orpheus Analytics</strong>  ELT pipelines → OLAP models → FastAPI delivery</li>
      <li><strong>SQL Analytics Portfolio</strong>  4 projects: window functions, cohort analysis, query optimization, star schema + Power BI</li>
      <li><strong>CPI Wage Disparity</strong>  econometric analysis across all US states (R, ARIMA, ANOVA)</li>
      <li><strong>NLP Topic Modeler</strong>  LDA on large-scale survey data (Python, Jupyter)</li>
    </ul>
    <button className="about-cta" onClick={() => navigate("/projects")}>
      View all projects →
    </button>
  </>
),
      },

      {
        id: "consultancy",
        title: "Data Consultancy",
        subtitle: "Ask me about it!",
        image: consultancyImg,
        tag: "Coming soon",
        position: { x: 78, y: 36 },
        details: (
    <>
      <p>
        Orpheus Analytics is two things simultaneously: a data consultancy
        delivering analytics engineering services to organizations, and a
        platform product that transforms public economic datasets into
        standardized, API-accessible intelligence.
      </p>

      <div style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>
          Consultancy
        </div>
        <p className="about-muted">
          Analytics engineering engagements for organizations that need
          production-grade data infrastructure, ELT pipelines, OLAP data
          models, Power BI semantic layers, and API-driven delivery systems.
          If your team is drowning in spreadsheets or your dashboards don't
          reflect what's actually happening; that's the problem I solve.
        </p>
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>
          Platform
        </div>
        <p className="about-muted">
          An end-to-end analytics platform ingesting public economic and
          supply chain datasets, transforming them through ELT pipelines and
          OLAP data models, and delivering curated economic indicators via
          a FastAPI + PostgreSQL + Docker API layer; making public data
          actually usable for analysts, researchers, and decision-makers.
        </p>
      </div>

      <p className="about-muted" style={{ marginTop: 12 }}>
        Platform in active architecture and development.
      </p>

      <button className="about-cta" onClick={() => navigate("/contact")}>
        Work with me →
      </button>
    </>
  ),
},
    ],
    [navigate]
  );

  // Clockwise navigation order in the modal:
  const order = useMemo(
    () => ["me", "origins", "byui", "exp_skills", "projects", "consultancy"],
    []
  );

  const activeIndex = activeId ? order.indexOf(activeId) : -1;
  const activeNode = activeId ? nodes.find((n) => n.id === activeId) : null;

  const goNext = () => {
    if (activeIndex < 0) return;
    const nextId = order[(activeIndex + 1) % order.length];
    setActiveId(nextId);
  };

  const goPrev = () => {
    if (activeIndex < 0) return;
    const prevId = order[(activeIndex - 1 + order.length) % order.length];
    setActiveId(prevId);
  };

  // Vanta NET background (About-only)
  useEffect(() => {
    if (!bgRef.current) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;

    vantaRef.current = NET({
      el: bgRef.current,
      THREE,
      mouseControls: true,
      touchControls: true,
      gyroControls: false,
      color: 0x62a5c9,
      backgroundColor: 0x0a1013,
      points: 12.0,
      maxDistance: 22.0,
      spacing: 18.0,
    });

    return () => {
      vantaRef.current?.destroy?.();
      vantaRef.current = null;
    };
  }, []);

  // Compute connection lines (from "me" to each other node)
  useEffect(() => {
    const compute = () => {
      if (!stageRef.current) return;
      const stageRect = stageRef.current.getBoundingClientRect();

      const leaderEl = nodeRefs.current["me"];
      const leaderCenter = getCenter(leaderEl);

      const newLines = nodes
        .filter((n) => n.id !== "me")
        .map((n) => {
          const el = nodeRefs.current[n.id];
          const c = getCenter(el);

          return {
            to: n.id,
            x1: leaderCenter.x - stageRect.left,
            y1: leaderCenter.y - stageRect.top,
            x2: c.x - stageRect.left,
            y2: c.y - stageRect.top,
          };
        });

      setLines(newLines);
    };

    const t = setTimeout(compute, 80);
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, { passive: true });

    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute);
    };
  }, [nodes]);

  // Keyboard controls for modal:
  // Esc closes, Left/Right cycles
  useEffect(() => {
    const onKey = (e) => {
      if (!activeId) return;

      if (e.key === "Escape") setActiveId(null);
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, activeIndex]);

  return (
    <div className="about-page">
      {/* NET background */}
      <div ref={bgRef} className="about-vanta" aria-hidden="true" />

      <div className="about-content">
        <div className="about-board">
          {/* Header inside board */}
          <div className="about-boardHeader">
            <div className="about-headerRow">
              <div>
                <h1>About Me</h1>
                <p>
                  A visual overview of how my background and experience connect. Click a card to explore the details..
                </p>
              </div>

              <button
                className="about-homeBtn"
                onClick={() => navigate("/")}
                aria-label="Go to Home"
                type="button"
              >
                Home
              </button>
            </div>
          </div>

          {/* Stage: lines + nodes */}
          <div ref={stageRef} className="about-stage">
            {/* Lines */}
            <svg className="about-lines" aria-hidden="true">
              {lines.map((l) => (
                <g key={l.to}>
                  <line
                    x1={l.x1}
                    y1={l.y1}
                    x2={l.x2}
                    y2={l.y2}
                    className="about-line"
                  />
                  <circle cx={l.x2} cy={l.y2} r="3.5" className="about-dot" />
                </g>
              ))}
            </svg>

            {/* Nodes */}
            {nodes.map((n) => {
              const isProfile = n.type === "profile";

              // IMPORTANT: no inline transform; CSS handles it + can override responsively
              const style = {
                left: `${clamp(n.position.x, 8, 92)}%`,
                top: `${clamp(n.position.y, 8, 92)}%`,
              };

              return (
                <button
                  key={n.id}
                  ref={(el) => (nodeRefs.current[n.id] = el)}
                  className={`about-node ${isProfile ? "about-node--profile" : ""}`}
                  style={style}
                  onClick={() => setActiveId(n.id)}
                  type="button"
                  aria-label={`Open ${n.title}`}
                  data-node={n.id}
                >
                  {isProfile ? (
                    <div className="about-profile">
                      <img className="about-avatar" src={n.image} alt={n.title} />
                      <div className="about-profileText">
                        <div className="about-name">{n.title}</div>
                        <div className="about-role">{n.subtitle}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="about-card">
                      <div className="about-cardTop">
                        <div className="about-cardTitle">{n.title}</div>
                        {n.tag ? <div className="about-pill">{n.tag}</div> : null}
                      </div>

                      <div className="about-cardSubtitle">{n.subtitle}</div>

                      {/* small thumbnail "evidence" */}
                      <div className="about-thumbRow">
                        <img
                          className="about-thumb"
                          src={n.image}
                          alt={`${n.title} image`}
                        />
                        <span className="about-thumbHint">Open details</span>
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modal with Prev/Next */}
      {activeNode ? (
        <div className="about-modalOverlay" onMouseDown={() => setActiveId(null)}>
          <div
            className="about-modal"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="about-modalHeader">
              <div>
                <div className="about-modalTitle">{activeNode.title}</div>
                <div className="about-modalSubtitle">{activeNode.subtitle}</div>
              </div>

              <div className="about-modalActions">
                <button className="about-navBtn" onClick={goPrev} type="button">
                  ← Prev
                </button>
                <button className="about-navBtn" onClick={goNext} type="button">
                  Next →
                </button>
                <button
                  className="about-close"
                  onClick={() => setActiveId(null)}
                  aria-label="Close"
                  type="button"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="about-modalBody">
              {/* hero image for each card */}
              {activeNode.image ? (
                <img
                  src={activeNode.image}
                  alt={`${activeNode.title} cover`}
                  className="about-modalHero"
                />
              ) : null}

              {activeNode.details}

              <div className="about-muted" style={{ marginTop: 12 }}>
                Tip: use ← → arrow keys to browse.
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

